/**
 * Configuração centralizada do provedor cartográfico e estilo vetorial do mapa (LegisCity).
 *
 * Arquitetura Cartográfica:
 * - Camada Primária (`osm-vector-local`, padrão ativo):
 *   Renderização vetorial em Canvas 2D dentro do Leaflet via `protomaps-leaflet`, consumindo
 *   o arquivo local `/maps/artur-nogueira.pmtiles` (extraído do OpenStreetMap para Artur Nogueira
 *   e municípios vizinhos nos níveis 0 a 15, com overzoom vetorial nítido até o zoom 19+).
 *   Zero dependência de servidores externos em tempo de execução, zero chaves de API e controle
 *   integral das cores de cada elemento (fundo, ruas locais, vias secundárias, rodovias,
 *   vegetação, água, edificações e topônimos) diretamente no código nos modos Claro e Escuro.
 * - Camada de Contingência (`osm-standard`, fallback automático):
 *   Caso o arquivo `.pmtiles` local ou servidor vetorial configurado falhe repetidamente,
 *   aciona automaticamente a camada raster OpenStreetMap Standard (`L.TileLayer`).
 *
 * Segurança:
 * - Variáveis `VITE_*` são compiladas no bundle público do cliente.
 * - `isValidTileUrlTemplate` e `isValidVectorSourceUrl` rejeitam URLs malformadas ou que
 *   contenham credenciais/chaves secretas na query string (`api_key`, `token`, `key`, `secret`).
 *
 * @module constants/mapConfig
 */

import {
  CenteredTextSymbolizer,
  CircleSymbolizer,
  GeomType,
  LineLabelSymbolizer,
  LineSymbolizer,
  OffsetTextSymbolizer,
  PolygonSymbolizer,
  ShieldSymbolizer,
  exp,
} from "protomaps-leaflet";

import { canonicalizeBairro, getRomanPhase, areSameNeighborhood } from "./neighborhoods.js";
import {
  THEMATIC_CATEGORIES,
  THEMATIC_CATEGORIES_ORDER,
  getCategoryStyle,
} from "./categories.js";

/** Coordenadas centrais padrão: Artur Nogueira, SP */
export const DEFAULT_CENTER = [-22.5714, -47.1728];

/** Zoom inicial padrão da visão geral do município */
export const DEFAULT_ZOOM = 13;

/** Parâmetros globais de zoom do mapa */
export const MAP_ZOOM_CONFIG = {
  minZoom: 0,
  maxZoom: 19,
  maxNativeZoom: 19,
  maxVectorDataZoom: 15,
};

/** URL local padrão do arquivo PMTiles de Artur Nogueira e região */
export const DEFAULT_VECTOR_PMTILES_URL = "/maps/artur-nogueira.pmtiles?v=20261023b";

/**
 * Paleta cartográfica vetorial do LegisCity — Modo Claro (inspirada no Transport Map / Design System).
 * Cada propriedade define a cor hexadecimal exata renderizada no Canvas 2D do mapa vetorial.
 */
export const MAP_VISUAL_PALETTE = {
  background: "#F8FAFC",
  localRoads: "#FFFFFF",
  localRoadCasing: "#DDE2E8",
  secondaryRoads: "#DDE2E8",
  secondaryRoadCasing: "#CBD5E1",
  majorRoads: "#BBC5D1",
  majorRoadCasing: "#94A3B8",
  railway: "#94A3B8",
  vegetation: "#DCE7E0",
  water: "#CBDCEB",
  waterLine: "#B4CDE3",
  urbanBlock: "#F1F5F9",
  buildings: "#EAEFF5",
  buildingStroke: "#DDE2E8",
  neighborhoodBoundaries: "#94A3B8",
  minorRoadLabel: "#475569",
  majorRoadLabel: "#334155",
  neighborhoodLabel: "#64748B",
  cityLabel: "#1E293B",
  waterLabel: "#4A7C9B",
  parkLabel: "#3B6E5C",
  publicPoiLabel: "#334155",
  publicPoiDot: "#64748B",
  labelHalo: "#F8FAFC",
  shieldBg: "#E2E8F0",
  shieldText: "#334155",
};

/**
 * Paleta cartográfica vetorial do LegisCity — Modo Escuro.
 * Garante contraste elevado para os clusters e nitidez vetorial até o zoom 19.
 */
export const MAP_DARK_VISUAL_PALETTE = {
  background: "#0F172A",
  localRoads: "#1E293B",
  localRoadCasing: "#334155",
  secondaryRoads: "#334155",
  secondaryRoadCasing: "#475569",
  majorRoads: "#475569",
  majorRoadCasing: "#64748B",
  railway: "#475569",
  vegetation: "#132A24",
  water: "#0F2942",
  waterLine: "#163B5C",
  urbanBlock: "#131C2E",
  buildings: "#19253A",
  buildingStroke: "#26354D",
  neighborhoodBoundaries: "#475569",
  minorRoadLabel: "#94A3B8",
  majorRoadLabel: "#CBD5E1",
  neighborhoodLabel: "#94A3B8",
  cityLabel: "#F1F5F9",
  waterLabel: "#60A5FA",
  parkLabel: "#6EE7B7",
  publicPoiLabel: "#CBD5E1",
  publicPoiDot: "#94A3B8",
  labelHalo: "#0F172A",
  shieldBg: "#1E293B",
  shieldText: "#CBD5E1",
};

/**
 * Retorna a paleta vetorial correspondente ao tema ativo (Claro ou Escuro).
 *
 * @param {boolean} [isDarkMode=false]
 * @returns {typeof MAP_VISUAL_PALETTE}
 */
export function getVectorMapPalette(isDarkMode = false) {
  return isDarkMode ? MAP_DARK_VISUAL_PALETTE : MAP_VISUAL_PALETTE;
}

/**
 * Constrói as regras de pintura vetorial (`paintRules`) do `protomaps-leaflet`
 * utilizando diretamente as cores da paleta do LegisCity.
 *
 * @param {boolean} [isDarkMode=false]
 * @returns {import("protomaps-leaflet").PaintRule[]}
 */
export function createVectorPaintRules(isDarkMode = false) {
  const p = getVectorMapPalette(isDarkMode);

  const isMajorRoad = (f) => {
    const kind = String(f?.props?.kind || "");
    const detail = String(f?.props?.kind_detail || "");
    if (f?.props?.is_municipal_road) return false;
    return (
      kind === "highway" ||
      (kind === "major_road" &&
        (detail === "motorway" || detail === "trunk" || detail === "primary"))
    );
  };

  const isSecondaryRoad = (f) => {
    if (isMajorRoad(f)) return false;
    const kind = String(f?.props?.kind || "");
    const detail = String(f?.props?.kind_detail || "");
    return (
      Boolean(f?.props?.is_municipal_road) ||
      kind === "major_road" ||
      kind === "medium_road" ||
      (kind === "minor_road" && (detail === "secondary" || detail === "tertiary"))
    );
  };

  const isLocalRoad = (f) => {
    const kind = String(f?.props?.kind || "");
    if (isMajorRoad(f) || isSecondaryRoad(f)) return false;
    return kind === "minor_road" || kind === "other" || kind === "path";
  };

  return [
    // 1. Terreno base (Earth)
    {
      dataLayer: "earth",
      symbolizer: new PolygonSymbolizer({
        fill: p.background,
      }),
    },

    // 2. Quadras urbanas, escolas, hospitais e áreas institucionais
    {
      dataLayer: "landuse",
      symbolizer: new PolygonSymbolizer({
        fill: p.urbanBlock,
      }),
      filter: (_z, f) => {
        const kind = String(f?.props?.kind || "");
        return [
          "residential",
          "commercial",
          "industrial",
          "hospital",
          "school",
          "university",
          "college",
          "pedestrian",
        ].includes(kind);
      },
    },

    // 3. Cobertura vegetal e áreas verdes (verde-sálvia claro e discreto)
    {
      dataLayer: "landcover",
      symbolizer: new PolygonSymbolizer({
        fill: p.vegetation,
        opacity: 0.75,
      }),
      filter: (_z, f) => {
        const kind = String(f?.props?.kind || "");
        return ["forest", "grassland", "scrub", "farmland"].includes(kind);
      },
    },
    {
      dataLayer: "landuse",
      symbolizer: new PolygonSymbolizer({
        fill: p.vegetation,
        opacity: 0.88,
      }),
      filter: (_z, f) => {
        const kind = String(f?.props?.kind || "");
        return [
          "park",
          "national_park",
          "nature_reserve",
          "protected_area",
          "forest",
          "wood",
          "grass",
          "grassland",
          "scrub",
          "village_green",
          "playground",
          "allotments",
          "cemetery",
          "golf_course",
        ].includes(kind);
      },
    },

    // 4. Corpos d'água (lagos, represas, rios e córregos)
    {
      dataLayer: "water",
      symbolizer: new PolygonSymbolizer({
        fill: p.water,
      }),
      filter: (_z, f) => f.geomType === GeomType.Polygon,
    },
    {
      dataLayer: "water",
      symbolizer: new LineSymbolizer({
        color: p.waterLine,
        width: (z) =>
          exp(1.6, [
            [10, 1],
            [14, 2.2],
            [18, 7],
          ])(z),
      }),
      filter: (_z, f) => f.geomType === GeomType.Line,
    },

    // 5. Edificações discretas em zoom aproximado (z >= 15)
    {
      dataLayer: "buildings",
      minzoom: 15,
      symbolizer: new PolygonSymbolizer({
        fill: p.buildings,
        stroke: p.buildingStroke,
        width: 0.5,
        opacity: 0.8,
      }),
    },

    // 6. Contornos (Casing) da malha viária — estilo Transport Map
    {
      dataLayer: "roads",
      minzoom: 12,
      symbolizer: new LineSymbolizer({
        color: p.localRoadCasing,
        width: (z) =>
          exp(1.6, [
            [12, 1.1],
            [14, 2.8],
            [16, 6.5],
            [18, 15],
            [19, 23],
          ])(z),
      }),
      filter: (_z, f) => isLocalRoad(f),
    },
    {
      dataLayer: "roads",
      minzoom: 11,
      symbolizer: new LineSymbolizer({
        color: p.secondaryRoadCasing,
        width: (z) =>
          exp(1.6, [
            [11, 1.4],
            [14, 4.0],
            [16, 8.2],
            [18, 18],
            [19, 27],
          ])(z),
      }),
      filter: (_z, f) => isSecondaryRoad(f),
    },
    {
      dataLayer: "roads",
      minzoom: 8,
      symbolizer: new LineSymbolizer({
        color: p.majorRoadCasing,
        width: (z) =>
          exp(1.6, [
            [8, 1.2],
            [12, 2.8],
            [14, 5.2],
            [16, 10.5],
            [18, 21],
            [19, 31],
          ])(z),
      }),
      filter: (_z, f) => isMajorRoad(f),
    },

    // 7. Preenchimento (Fill) da malha viária por hierarquia
    {
      dataLayer: "roads",
      minzoom: 12,
      symbolizer: new LineSymbolizer({
        color: p.localRoads,
        width: (z) =>
          exp(1.6, [
            [12, 0.5],
            [14, 1.6],
            [16, 4.6],
            [18, 12],
            [19, 19],
          ])(z),
      }),
      filter: (_z, f) => isLocalRoad(f),
    },
    {
      dataLayer: "roads",
      minzoom: 11,
      symbolizer: new LineSymbolizer({
        color: p.secondaryRoads,
        width: (z) =>
          exp(1.6, [
            [11, 0.9],
            [14, 2.6],
            [16, 6.2],
            [18, 14.5],
            [19, 22],
          ])(z),
      }),
      filter: (_z, f) => isSecondaryRoad(f),
    },
    {
      dataLayer: "roads",
      minzoom: 7,
      symbolizer: new LineSymbolizer({
        color: p.majorRoads,
        width: (z) =>
          exp(1.6, [
            [7, 0.8],
            [12, 1.8],
            [14, 3.6],
            [16, 8.0],
            [18, 17],
            [19, 26],
          ])(z),
      }),
      filter: (_z, f) => isMajorRoad(f),
    },

    // 8. Ferrovias e corredores de transporte
    {
      dataLayer: "roads",
      minzoom: 11,
      symbolizer: new LineSymbolizer({
        color: p.railway,
        dash: [3, 3],
        width: (z) =>
          exp(1.6, [
            [11, 0.8],
            [15, 1.8],
            [19, 4],
          ])(z),
        opacity: 0.7,
      }),
      filter: (_z, f) => f?.props?.kind === "rail",
    },

    // 9. Limites administrativos e de bairros oficiais (GPKG mapa_abairramento + OSM)
    {
      dataLayer: "boundaries",
      minzoom: 14,
      symbolizer: new LineSymbolizer({
        color: p.neighborhoodBoundaries,
        width: 0.85,
        dash: [3, 3],
        opacity: 0.42,
      }),
      filter: (_z, f) => f?.props?.kind === "neighbourhood",
    },
    {
      dataLayer: "boundaries",
      symbolizer: new LineSymbolizer({
        color: p.neighborhoodBoundaries,
        width: 1.2,
        dash: [5, 3],
        opacity: 0.65,
      }),
      filter: (_z, f) => f?.props?.kind !== "neighbourhood",
    },

    // 10. Marcadores discretos para POIs públicos municipais do GPKG
    {
      dataLayer: "pois",
      minzoom: 13,
      symbolizer: new CircleSymbolizer({
        radius: (z, f) => (z >= 15 ? 2.8 : f?.props?.poi_category === "landmark" ? 3.2 : 2.6),
        fill: p.publicPoiDot,
        stroke: p.labelHalo,
        width: 1.4,
        opacity: 0.9,
      }),
      filter: (z, f) => {
        if (f?.props?.source !== "gpkg") return false;
        const cat = String(f?.props?.poi_category || "");
        if (z === 13) return cat === "landmark" || cat === "rural_poi";
        if (z === 14) return cat === "landmark" || cat === "rural_poi" || cat === "public_service" || cat === "park_square";
        return true;
      },
    },
  ];
}

/**
 * Constrói as regras de rotulagem (`labelRules`) do `protomaps-leaflet`
 * priorizando a base cartográfica municipal de Artur Nogueira (GPKG):
 * estradas municipais (ATNs/vicinais), rodovias, prédios públicos, praças e os 132 bairros oficiais.
 *
 * @param {boolean} [isDarkMode=false]
 * @param {string} [lang="pt"]
 * @returns {import("protomaps-leaflet").LabelRule[]}
 */
export function createVectorLabelRules(isDarkMode = false, lang = "pt") {
  const p = getVectorMapPalette(isDarkMode);
  const nametags = [`name:${lang}`, "name"];

  return [
    // 1. Rótulos de municípios (Artur Nogueira, Cosmópolis, Holambra, Engenheiro Coelho)
    {
      id: "city_labels",
      dataLayer: "places",
      minzoom: 8,
      maxzoom: 15,
      symbolizer: new CenteredTextSymbolizer({
        labelProps: nametags,
        fill: p.cityLabel,
        stroke: p.labelHalo,
        width: 3,
        lineHeight: 1.4,
        font: (z) => (z >= 13 ? "700 13.5px Inter, sans-serif" : "700 12px Inter, sans-serif"),
      }),
      filter: (_z, f) => String(f?.props?.kind || "") === "locality",
    },

    // 2. Nomes de rodovias, estradas municipais (ATNs/vicinais) e avenidas principais (curvos na via)
    {
      id: "major_road_labels",
      dataLayer: "roads",
      minzoom: 11,
      symbolizer: new LineLabelSymbolizer({
        labelProps: nametags,
        fill: p.majorRoadLabel,
        stroke: p.labelHalo,
        width: 3,
        font: (z) => (z >= 16 ? "600 12.5px Inter, sans-serif" : "600 11px Inter, sans-serif"),
        repeatDistance: 200,
      }),
      filter: (_z, f) => {
        const kind = String(f?.props?.kind || "");
        return (
          Boolean(f?.props?.is_municipal_road) ||
          kind === "highway" ||
          kind === "major_road" ||
          kind === "medium_road"
        );
      },
    },

    // 3. Âncoras de estradas municipais/vicinais em visão geral (z=11..14) quando o trecho é curto para LineLabel
    {
      id: "municipal_road_anchor_labels",
      dataLayer: "places",
      minzoom: 11,
      maxzoom: 14,
      symbolizer: new CenteredTextSymbolizer({
        labelProps: nametags,
        fill: p.majorRoadLabel,
        stroke: p.labelHalo,
        width: 2.8,
        maxLineChars: 24,
        lineHeight: 1.45,
        font: "600 10.5px Inter, sans-serif",
      }),
      filter: (_z, f) => String(f?.props?.kind || "") === "municipal_road_label",
    },

    // 4. Escudos de rodovias e estradas municipais (ex.: SP-332, SP-107, ATN-142, ATN-050, ATN-030)
    {
      id: "highway_shields",
      dataLayer: "roads",
      minzoom: 11,
      symbolizer: new ShieldSymbolizer({
        labelProps: ["ref"],
        font: "600 10px Inter, sans-serif",
        background: p.shieldBg,
        fill: p.shieldText,
        padding: 3,
      }),
      filter: (_z, f) => {
        const kind = String(f?.props?.kind || "");
        const ref = String(f?.props?.ref || "");
        return (
          (kind === "highway" || kind === "major_road" || Boolean(f?.props?.is_municipal_road)) &&
          Boolean(ref) &&
          ref.length <= 8
        );
      },
    },

    // 5. Marcos municipais e utilidade pública prioritários do GPKG (Paço, Câmara, Hospital, Rodoviária, Balneário, Lagoa, Rural - z >= 13)
    {
      id: "gpkg_landmark_pois",
      dataLayer: "pois",
      minzoom: 13,
      sort: (a, b) => (Number(a?.sort_rank) || 60) - (Number(b?.sort_rank) || 60),
      symbolizer: new OffsetTextSymbolizer({
        labelProps: nametags,
        fill: (z, f) =>
          f?.props?.kind === "park" || /Lagoa|Balneário|Praça/i.test(String(f?.props?.name || ""))
            ? p.parkLabel
            : p.publicPoiLabel,
        stroke: p.labelHalo,
        width: 2.6,
        offsetX: 5,
        offsetY: 4,
        maxLineChars: 18,
        lineHeight: 1.45,
        font: (z) => (z >= 15 ? "600 11px Inter, sans-serif" : "600 10px Inter, sans-serif"),
      }),
      filter: (z, f) => {
        if (f?.props?.source !== "gpkg") return false;
        const cat = String(f?.props?.poi_category || "");
        return cat === "landmark" || cat === "rural_poi";
      },
    },

    // 6. Prédios públicos essenciais (UBSs, USFs, Escolas Polos, Praças, Fórum, Delegacia - z >= 14)
    {
      id: "gpkg_public_service_pois",
      dataLayer: "pois",
      minzoom: 14,
      sort: (a, b) => (Number(a?.sort_rank) || 95) - (Number(b?.sort_rank) || 95),
      symbolizer: new OffsetTextSymbolizer({
        labelProps: nametags,
        fill: (_z, f) => (f?.props?.poi_category === "park_square" ? p.parkLabel : p.publicPoiLabel),
        stroke: p.labelHalo,
        width: 2.4,
        offsetX: 5,
        offsetY: 4,
        maxLineChars: 18,
        lineHeight: 1.45,
        font: (z) => (z >= 16 ? "600 11px Inter, sans-serif" : "500 10px Inter, sans-serif"),
      }),
      filter: (z, f) => {
        if (f?.props?.source !== "gpkg") return false;
        const cat = String(f?.props?.poi_category || "");
        if (cat !== "public_service" && cat !== "park_square") return false;
        if (z === 14) {
          // No zoom 14: priorizar Saúde (UBS/USF), Educação polo (EMEF) e Praças principais
          return Number(f?.props?.sort_rank) <= 130 || cat === "park_square";
        }
        return true;
      },
    },

    // 7. Rótulos dos 132 bairros e loteamentos oficiais (GPKG `mapa_abairramento` com prioridade sobre OSM)
    {
      id: "neighbourhood_labels",
      dataLayer: "places",
      minzoom: 12,
      sort: (a, b) => (Number(a?.sort_rank) || 400) - (Number(b?.sort_rank) || 400),
      symbolizer: new CenteredTextSymbolizer({
        labelProps: nametags,
        fill: p.neighborhoodLabel,
        stroke: p.labelHalo,
        width: 2.4,
        maxLineChars: 14,
        lineHeight: 1.85,
        font: (z) => (z >= 15 ? "600 10px Inter, sans-serif" : "600 9px Inter, sans-serif"),
        textTransform: "uppercase",
      }),
      filter: (_z, f) => {
        const kind = String(f?.props?.kind || "");
        return ["neighbourhood", "suburb", "quarter", "macrohood"].includes(kind);
      },
    },

    // 8. Nomes de ruas locais e loteamentos (GPKG `nome_ruas` + `complemento_faces_de_logradouro` + OSM, z=14..19+)
    {
      id: "minor_road_labels",
      dataLayer: "roads",
      minzoom: 14,
      symbolizer: new LineLabelSymbolizer({
        labelProps: nametags,
        fill: p.minorRoadLabel,
        stroke: p.labelHalo,
        width: 2.5,
        font: (z) => (z >= 17 ? "500 12px Inter, sans-serif" : "500 10.5px Inter, sans-serif"),
        repeatDistance: 175,
      }),
      filter: (_z, f) => {
        if (f?.props?.is_municipal_road) return false;
        const kind = String(f?.props?.kind || "");
        return kind === "minor_road" || kind === "other";
      },
    },

    // 9. Rótulos de corpos d'água e lagoas
    {
      id: "water_labels",
      dataLayer: "water",
      minzoom: 13,
      symbolizer: new CenteredTextSymbolizer({
        labelProps: nametags,
        fill: p.waterLabel,
        stroke: p.labelHalo,
        width: 2.5,
        lineHeight: 1.4,
        font: "500 11px Inter, sans-serif",
      }),
      filter: (_z, f) => Boolean(f?.props?.name),
    },

    // 10. Rótulos de parques e áreas verdes complementares do OSM (quando não sobrepostos ao GPKG)
    {
      id: "park_labels",
      dataLayer: "pois",
      minzoom: 14,
      symbolizer: new CenteredTextSymbolizer({
        labelProps: nametags,
        fill: p.parkLabel,
        stroke: p.labelHalo,
        width: 2.5,
        lineHeight: 1.4,
        font: "500 10.5px Inter, sans-serif",
      }),
      filter: (_z, f) => {
        if (f?.props?.source === "gpkg") return false;
        const kind = String(f?.props?.kind || "");
        return ["park", "nature_reserve", "playground", "stadium", "sports_centre"].includes(kind);
      },
    },
  ];
}

/**
 * Tile transparente 1x1 (GIF base64) utilizado em `errorTileUrl` da camada de contingência
 * exclusivamente para evitar ícones de imagem quebrada.
 */
export const TRANSPARENT_ERROR_TILE_URL =
  "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7";

/** Quantidade de erros consecutivos de carregamento de tiles para acionar o fallback */
export const TILE_ERROR_FALLBACK_THRESHOLD = 4;

/** Expressão regular para detectar parâmetros sensíveis/segredos em URLs públicas do frontend */
const SECRET_QUERY_PARAM_REGEX =
  /(?:^|[?&])(api[-_]?key|access[-_]?token|auth[-_]?token|client[-_]?secret|token|key|secret|password|passwd|sig|signature)=/i;

/**
 * Catálogo centralizado de provedores homologados.
 */
export const TILE_PROVIDERS = {
  "osm-vector-local": {
    id: "osm-vector-local",
    name: "OpenStreetMap Vetorial Local (PMTiles)",
    type: "vector",
    lightUrl: DEFAULT_VECTOR_PMTILES_URL,
    darkUrl: DEFAULT_VECTOR_PMTILES_URL,
    subdomains: "",
    attribution:
      '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener noreferrer">OpenStreetMap</a> contribuidores',
    minZoom: MAP_ZOOM_CONFIG.minZoom,
    maxZoom: MAP_ZOOM_CONFIG.maxZoom,
    maxNativeZoom: MAP_ZOOM_CONFIG.maxNativeZoom,
    maxDataZoom: MAP_ZOOM_CONFIG.maxVectorDataZoom,
    darkModeClassName: "",
    requiresApiKey: false,
  },
  "esri-light-topo": {
    id: "esri-light-topo",
    name: "Esri World Topographic (Light Neutral)",
    type: "raster",
    lightUrl:
      "https://server.arcgisonline.com/ArcGIS/rest/services/World_Topo_Map/MapServer/tile/{z}/{y}/{x}",
    darkUrl:
      "https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Base/MapServer/tile/{z}/{y}/{x}",
    subdomains: "",
    attribution:
      'Tiles &copy; <a href="https://www.esri.com" target="_blank" rel="noopener noreferrer">Esri</a> &mdash; Source: Esri, HERE, Garmin, USGS, Intermap, INCREMENT P, NRCan, Esri Japan, METI, &copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener noreferrer">OpenStreetMap</a> contributors, and the GIS User Community',
    minZoom: MAP_ZOOM_CONFIG.minZoom,
    maxZoom: MAP_ZOOM_CONFIG.maxZoom,
    maxNativeZoom: 19,
    darkMaxNativeZoom: 16,
    darkModeClassName: "",
    requiresApiKey: false,
  },
  "carto-positron": {
    id: "carto-positron",
    name: "CARTO Positron",
    type: "raster",
    lightUrl: "https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png",
    darkUrl: "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png",
    subdomains: "abcd",
    attribution:
      '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener noreferrer">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions" target="_blank" rel="noopener noreferrer">CARTO</a>',
    minZoom: MAP_ZOOM_CONFIG.minZoom,
    maxZoom: MAP_ZOOM_CONFIG.maxZoom,
    maxNativeZoom: MAP_ZOOM_CONFIG.maxNativeZoom,
    darkModeClassName: "",
    requiresApiKey: true,
  },
  "osm-standard": {
    id: "osm-standard",
    name: "OpenStreetMap Standard",
    type: "raster",
    lightUrl: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
    darkUrl: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
    subdomains: "abc",
    attribution:
      '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener noreferrer">OpenStreetMap</a> contribuidores',
    minZoom: MAP_ZOOM_CONFIG.minZoom,
    maxZoom: MAP_ZOOM_CONFIG.maxZoom,
    maxNativeZoom: MAP_ZOOM_CONFIG.maxNativeZoom,
    darkModeClassName: "osm-dark-tiles",
    requiresApiKey: false,
  },
};

export const DEFAULT_TILE_PROVIDER_ID = "osm-vector-local";
export const FALLBACK_TILE_PROVIDER_ID = "osm-standard";

/**
 * Verifica se uma string de URL contém segredos, chaves de API ou credenciais embutidas
 * que não devem ser expostas no bundle público do cliente (`VITE_*`).
 *
 * @param {string} rawUrl
 * @returns {boolean}
 */
export function containsEmbeddedSecret(rawUrl) {
  if (typeof rawUrl !== "string" || !rawUrl.trim()) return false;
  const trimmed = rawUrl.trim();

  if (SECRET_QUERY_PARAM_REGEX.test(trimmed)) {
    return true;
  }

  if (/^https?:\/\/[^/?#]+:[^/?#]+@/i.test(trimmed)) {
    return true;
  }

  return false;
}

/**
 * Valida se um template de URL de tiles (ou arquivo `.pmtiles`) é seguro e estruturalmente válido.
 *
 * @param {string} rawUrl
 * @returns {boolean}
 */
export function isValidTileUrlTemplate(rawUrl) {
  if (typeof rawUrl !== "string") return false;
  const trimmed = rawUrl.trim();
  if (!trimmed) return false;

  const isHttpUrl = /^https?:\/\/[^\s/$.?#].[^\s]*$/i.test(trimmed);
  const isRelativeProxyUrl = /^\/[^\s]*$/.test(trimmed) && !trimmed.startsWith("//");
  if (!isHttpUrl && !isRelativeProxyUrl) {
    return false;
  }

  if (containsEmbeddedSecret(trimmed)) {
    return false;
  }

  // Aceita arquivos .pmtiles estáticos (com ou sem query string de versão) ou templates com {z}/{x}/{y}
  if (trimmed.split("?")[0].endsWith(".pmtiles")) {
    return true;
  }

  const hasZ = trimmed.includes("{z}");
  const hasX = trimmed.includes("{x}");
  const hasY = trimmed.includes("{y}") || trimmed.includes("{-y}");
  return hasZ && hasX && hasY;
}

function resolveValidatedCustomUrl(rawUrl) {
  if (typeof rawUrl !== "string") return "";
  const trimmed = rawUrl.trim();
  if (!trimmed) return "";
  return isValidTileUrlTemplate(trimmed) ? trimmed : "";
}

/**
 * Verifica se a URL carregada em um evento `tileload` corresponde ao GIF transparente de erro
 * (`errorTileUrl`) ou a um data URI paliativo, evitando interpretá-lo como recuperação de rede.
 *
 * @param {string} [loadedSrc]
 * @param {string} [errorTileUrl]
 * @returns {boolean}
 */
export function isErrorTileSource(loadedSrc, errorTileUrl = TRANSPARENT_ERROR_TILE_URL) {
  const normalized = typeof loadedSrc === "string" ? loadedSrc.trim() : "";
  if (!normalized) return true;
  if (normalized === errorTileUrl || normalized === TRANSPARENT_ERROR_TILE_URL) return true;
  if (normalized.startsWith("data:image/")) return true;
  return false;
}

/**
 * Redutor puro para gerenciar a saúde da camada de tiles e o acionamento de fallback.
 */
export function reduceTileLayerHealth(
  state = { useFallbackTiles: false, tileLayerUnavailable: false, consecutiveErrors: 0 },
  event = { type: "reset" },
  config = {
    hasFallback: true,
    errorTileUrl: TRANSPARENT_ERROR_TILE_URL,
    threshold: TILE_ERROR_FALLBACK_THRESHOLD,
  }
) {
  const threshold =
    typeof config.threshold === "number" && config.threshold >= 1
      ? config.threshold
      : TILE_ERROR_FALLBACK_THRESHOLD;
  const errorTileUrl = config.errorTileUrl || TRANSPARENT_ERROR_TILE_URL;

  if (event.type === "reset") {
    return {
      ...state,
      consecutiveErrors: 0,
    };
  }

  if (event.type === "tileload") {
    if (isErrorTileSource(event.src, errorTileUrl)) {
      return state;
    }
    if (state.consecutiveErrors === 0 && !state.tileLayerUnavailable) {
      return state;
    }
    return {
      ...state,
      consecutiveErrors: 0,
      tileLayerUnavailable: false,
    };
  }

  if (event.type === "tileerror") {
    const nextErrors = state.consecutiveErrors + 1;
    if (nextErrors < threshold) {
      return {
        ...state,
        consecutiveErrors: nextErrors,
      };
    }

    if (!state.useFallbackTiles && config.hasFallback) {
      return {
        useFallbackTiles: true,
        tileLayerUnavailable: false,
        consecutiveErrors: 0,
      };
    }

    return {
      ...state,
      consecutiveErrors: nextErrors,
      tileLayerUnavailable: true,
    };
  }

  return state;
}

/**
 * Resolve a configuração ativa para a camada base do mapa (vetorial PMTiles ou raster `<TileLayer />`).
 *
 * @param {object} [options]
 * @param {boolean} [options.isDarkMode=false] - Se o tema escuro está ativo.
 * @param {boolean} [options.useFallback=false] - Se deve usar o provedor secundário de contingência.
 * @param {Record<string, any>} [options.env] - Objeto de ambiente (padrão: `import.meta.env`).
 */
export function getTileLayerConfig({
  isDarkMode = false,
  useFallback = false,
  env = typeof import.meta !== "undefined" && import.meta.env ? import.meta.env : {},
} = {}) {
  const configuredProviderId = String(env.VITE_MAP_TILE_PROVIDER || DEFAULT_TILE_PROVIDER_ID)
    .trim()
    .toLowerCase();

  const primaryProvider =
    TILE_PROVIDERS[configuredProviderId] || TILE_PROVIDERS[DEFAULT_TILE_PROVIDER_ID];
  const fallbackProvider = TILE_PROVIDERS[FALLBACK_TILE_PROVIDER_ID];

  if (useFallback) {
    const customFallbackUrl = resolveValidatedCustomUrl(env.VITE_MAP_TILE_FALLBACK_URL);
    const customFallbackAttribution = String(env.VITE_MAP_TILE_FALLBACK_ATTRIBUTION || "").trim();
    const fallbackUrl =
      customFallbackUrl || (isDarkMode ? fallbackProvider.darkUrl : fallbackProvider.lightUrl);
    const fallbackAttribution =
      customFallbackUrl && customFallbackAttribution
        ? customFallbackAttribution
        : fallbackProvider.attribution;

    return {
      key: `${fallbackProvider.id}-${isDarkMode ? "dark" : "light"}-fallback`,
      providerId: fallbackProvider.id,
      providerName: fallbackProvider.name,
      isVector: false,
      url: fallbackUrl,
      attribution: fallbackAttribution,
      subdomains: fallbackProvider.subdomains,
      minZoom: fallbackProvider.minZoom,
      maxZoom: fallbackProvider.maxZoom,
      maxNativeZoom: fallbackProvider.maxNativeZoom,
      maxDataZoom: fallbackProvider.maxNativeZoom,
      errorTileUrl: TRANSPARENT_ERROR_TILE_URL,
      className: isDarkMode ? fallbackProvider.darkModeClassName : "",
      hasFallback: false,
    };
  }

  const rawLightUrl = String(env.VITE_MAP_TILE_URL || "").trim();
  const rawDarkUrl = String(env.VITE_MAP_TILE_DARK_URL || "").trim();
  const customLightUrl = resolveValidatedCustomUrl(rawLightUrl);
  const customDarkUrl = resolveValidatedCustomUrl(rawDarkUrl);
  const hadRejectedCustomUrl =
    (Boolean(rawLightUrl) && !customLightUrl) || (Boolean(rawDarkUrl) && !customDarkUrl);

  const customAttribution = String(env.VITE_MAP_TILE_ATTRIBUTION || "").trim();
  const customSubdomains =
    env.VITE_MAP_TILE_SUBDOMAINS !== undefined
      ? String(env.VITE_MAP_TILE_SUBDOMAINS).trim()
      : null;
  const customMaxZoom = Number(env.VITE_MAP_TILE_MAX_ZOOM);

  const activeCustomUrl = isDarkMode ? customDarkUrl || customLightUrl : customLightUrl;
  const url = activeCustomUrl || (isDarkMode ? primaryProvider.darkUrl : primaryProvider.lightUrl);
  const isVector =
    url.split("?")[0].endsWith(".pmtiles") ||
    url.endsWith(".mvt") ||
    url.endsWith(".pbf") ||
    (!activeCustomUrl && primaryProvider.type === "vector");

  const attribution =
    !hadRejectedCustomUrl && customAttribution ? customAttribution : primaryProvider.attribution;

  const subdomains =
    customSubdomains !== null && !hadRejectedCustomUrl
      ? customSubdomains
      : url.includes("{s}")
      ? primaryProvider.subdomains || "abc"
      : "";

  const maxZoom =
    Number.isFinite(customMaxZoom) && customMaxZoom > 0
      ? customMaxZoom
      : primaryProvider.maxZoom;

  const nativeZoom =
    isDarkMode && primaryProvider.darkMaxNativeZoom && !activeCustomUrl
      ? primaryProvider.darkMaxNativeZoom
      : primaryProvider.maxNativeZoom;

  const className =
    !isVector && isDarkMode && !customDarkUrl && customLightUrl
      ? "osm-dark-tiles"
      : !isVector && isDarkMode
      ? primaryProvider.darkModeClassName
      : "";

  const hasFallback = primaryProvider.id !== fallbackProvider.id || Boolean(activeCustomUrl);

  return {
    key: `${primaryProvider.id}${activeCustomUrl ? "-custom" : ""}`,
    providerId: activeCustomUrl ? "custom" : primaryProvider.id,
    providerName: activeCustomUrl ? "Custom Tile Provider" : primaryProvider.name,
    isVector,
    url,
    attribution,
    subdomains,
    minZoom: primaryProvider.minZoom,
    maxZoom,
    maxNativeZoom: Math.min(nativeZoom, maxZoom),
    maxDataZoom: primaryProvider.maxDataZoom || MAP_ZOOM_CONFIG.maxVectorDataZoom,
    errorTileUrl: TRANSPARENT_ERROR_TILE_URL,
    className,
    hasFallback,
  };
}


/**
 * Verifica se uma feição GeoJSON corresponde ao bairro selecionado (por ID ou nome).
 *
 * @param {any} feature Feição GeoJSON do bairro
 * @param {any} selectedNeighborhood Bairro selecionado (objeto com id/name ou string com nome)
 * @returns {boolean}
 */
export function isNeighborhoodSelected(feature, selectedNeighborhood) {
  if (!selectedNeighborhood || !feature) return false;

  // 1. Casamento direto por ID
  if (typeof selectedNeighborhood === "number") {
    if (feature.id === selectedNeighborhood) return true;
  } else if (typeof selectedNeighborhood === "object" && selectedNeighborhood !== null) {
    if (
      selectedNeighborhood.id !== undefined &&
      selectedNeighborhood.id !== null &&
      feature.id !== undefined &&
      feature.id !== null
    ) {
      if (Number(selectedNeighborhood.id) === Number(feature.id)) return true;
    }
  } else if (typeof selectedNeighborhood === "string" && /^\d+$/.test(selectedNeighborhood.trim())) {
    if (Number(selectedNeighborhood.trim()) === Number(feature.id)) return true;
  }

  const targetName = typeof selectedNeighborhood === "string"
    ? selectedNeighborhood.trim()
    : String(selectedNeighborhood.name || selectedNeighborhood.fullName || "").trim();

  if (!targetName) return false;

  const targetLower = targetName.toLowerCase();
  const fName = String(feature.properties?.name || "").trim().toLowerCase();
  const fFullName = String(feature.properties?.fullName || "").trim().toLowerCase();
  const fAliases = (feature.properties?.aliases || []).map((a) => String(a).trim().toLowerCase());

  // 2. Igualdade exata no nome limpo, nome completo ou lista de apelidos oficiais
  if (fName && fName === targetLower) return true;
  if (fFullName && fFullName === targetLower) return true;
  if (fAliases.includes(targetLower)) return true;

  // 3. Unificação Canônica (ex: 'Bairrinha' e 'Bairrinho', 'Jardim de Faveri' e 'Jardim Faveri')
  if (areSameNeighborhood(targetName, fName || fFullName)) return true;

  // 4. Verificação estrita de fase/numeração romana (I vs II vs III)
  // Bairros como Jardim Sacilotto I e II, Planalto I e II são áreas diferentes e NUNCA devem colidir
  const targetPhase = getRomanPhase(targetName);
  const fPhase = getRomanPhase(fName) || getRomanPhase(fFullName);

  if (targetPhase || fPhase) {
    if (targetPhase !== fPhase) return false;
  }

  // 5. Casamento por substring seguro somente quando a fase/numeração confere ou ambos não têm fase
  if (fName && targetLower.length >= 4 && (fName.includes(targetLower) || targetLower.includes(fName))) {
    return true;
  }
  if (fFullName && targetLower.length >= 4 && (fFullName.includes(targetLower) || targetLower.includes(fFullName))) {
    return true;
  }

  return false;
}

/**
 * Retorna o estilo Leaflet para cada feição de bairro com base no estado de seleção e tema.
 *
 * @param {any} feature Feição GeoJSON do bairro
 * @param {any} selectedNeighborhood Bairro atualmente selecionado (objeto ou string)
 * @param {boolean} [isDarkMode=false] Tema ativo
 * @returns {import("leaflet").PathOptions}
 */
export function getNeighborhoodFeatureStyle(feature, selectedNeighborhood, isDarkMode = false) {
  const isSelected = isNeighborhoodSelected(feature, selectedNeighborhood);

  if (isSelected) {
    return {
      fillColor: isDarkMode ? "#38bdf8" : "#2563eb",
      fillOpacity: isDarkMode ? 0.22 : 0.15,
      color: isDarkMode ? "#60a5fa" : "#1d4ed8",
      weight: 2.2,
      opacity: 0.9,
      dashArray: undefined,
      className: "neighborhood-highlight-active",
    };
  }

  return {
    fillColor: "#000000",
    fillOpacity: 0.0001,
    color: "transparent",
    weight: 1,
    opacity: 0,
    className: "neighborhood-interactive-hit-area",
  };
}

/**
 * Limites geográficos válidos do município de Artur Nogueira e entorno imediato.
 * Utilizado para descartar coordenadas nulas, (0,0) ou fora do município antes do Point-in-Polygon.
 */
export const ARTUR_NOGUEIRA_BOUNDS = {
  minLat: -22.75,
  maxLat: -22.45,
  minLon: -47.35,
  maxLon: -47.05,
};

/**
 * Cores de contorno (ciano/azul-claro de alto contraste) reservadas exclusivamente
 * para destacar o bairro selecionado sobre a escala Azul → Índigo sem alterar sua classe.
 */
export const CHOROPLETH_SELECTED_STROKE = {
  light: "#06B6D4",
  dark: "#22D3EE",
};

/**
 * Paleta sequencial analítica de 5 faixas discretas + faixa neutra (0) — Modo Claro (Azul-claro → Azul → Índigo).
 * As faixas inferiores utilizam bordas com contraste reforçado para não desaparecerem sobre vias e áreas verdes.
 */
export const CHOROPLETH_PALETTE_LIGHT = [
  {
    classIndex: 0,
    name: "Sem indicações",
    fillColor: "#E5E7EB",
    fillOpacity: 0.42,
    borderColor: "#94A3B8",
    badgeTextColor: "#475569",
  },
  {
    classIndex: 1,
    name: "Muito Baixa",
    fillColor: "#DBEAFE",
    fillOpacity: 0.64,
    borderColor: "#60A5FA",
    badgeTextColor: "#1E40AF",
  },
  {
    classIndex: 2,
    name: "Baixa",
    fillColor: "#93C5FD",
    fillOpacity: 0.68,
    borderColor: "#3B82F6",
    badgeTextColor: "#1E3A8A",
  },
  {
    classIndex: 3,
    name: "Média",
    fillColor: "#3B82F6",
    fillOpacity: 0.72,
    borderColor: "#1D4ED8",
    badgeTextColor: "#FFFFFF",
  },
  {
    classIndex: 4,
    name: "Alta",
    fillColor: "#1E40AF",
    fillOpacity: 0.78,
    borderColor: "#1E3A8A",
    badgeTextColor: "#FFFFFF",
  },
  {
    classIndex: 5,
    name: "Muito Alta",
    fillColor: "#0B1F6B",
    fillOpacity: 0.84,
    borderColor: "#071447",
    badgeTextColor: "#FFFFFF",
  },
];

/**
 * Paleta sequencial analítica de 5 faixas discretas + faixa neutra (0) — Modo Escuro.
 * Mantém a progressão em azul, com tons mais luminosos para as classes inferiores e azuis intensos para as superiores.
 */
export const CHOROPLETH_PALETTE_DARK = [
  {
    classIndex: 0,
    name: "Sem indicações",
    fillColor: "#1E293B",
    fillOpacity: 0.42,
    borderColor: "#475569",
    badgeTextColor: "#94A3B8",
  },
  {
    classIndex: 1,
    name: "Muito Baixa",
    fillColor: "#BFDBFE",
    fillOpacity: 0.50,
    borderColor: "#93C5FD",
    badgeTextColor: "#0F172A",
  },
  {
    classIndex: 2,
    name: "Baixa",
    fillColor: "#93C5FD",
    fillOpacity: 0.58,
    borderColor: "#60A5FA",
    badgeTextColor: "#0F172A",
  },
  {
    classIndex: 3,
    name: "Média",
    fillColor: "#3B82F6",
    fillOpacity: 0.68,
    borderColor: "#93C5FD",
    badgeTextColor: "#FFFFFF",
  },
  {
    classIndex: 4,
    name: "Alta",
    fillColor: "#1D4ED8",
    fillOpacity: 0.78,
    borderColor: "#60A5FA",
    badgeTextColor: "#FFFFFF",
  },
  {
    classIndex: 5,
    name: "Muito Alta",
    fillColor: "#1E3A8A",
    fillOpacity: 0.86,
    borderColor: "#3B82F6",
    badgeTextColor: "#FFFFFF",
  },
];

/**
 * Retorna a paleta coroplética correspondente ao tema ativo.
 *
 * @param {boolean} [isDarkMode=false]
 * @returns {typeof CHOROPLETH_PALETTE_LIGHT}
 */
export function getChoroplethPalette(isDarkMode = false) {
  return isDarkMode ? CHOROPLETH_PALETTE_DARK : CHOROPLETH_PALETTE_LIGHT;
}

/**
 * Valida se um par (latitude, longitude) é numérico finito e está contido nos limites de Artur Nogueira.
 *
 * @param {any} lat
 * @param {any} lon
 * @returns {boolean}
 */
export function isValidMunicipalCoordinate(lat, lon) {
  if (
    lat === null ||
    lat === undefined ||
    lon === null ||
    lon === undefined ||
    typeof lat === "boolean" ||
    typeof lon === "boolean" ||
    lat === "" ||
    lon === ""
  ) {
    return false;
  }
  const numLat = Number(lat);
  const numLon = Number(lon);
  if (!Number.isFinite(numLat) || !Number.isFinite(numLon)) return false;

  return (
    numLat >= ARTUR_NOGUEIRA_BOUNDS.minLat &&
    numLat <= ARTUR_NOGUEIRA_BOUNDS.maxLat &&
    numLon >= ARTUR_NOGUEIRA_BOUNDS.minLon &&
    numLon <= ARTUR_NOGUEIRA_BOUNDS.maxLon
  );
}

/**
 * Verifica se o ponto (lon, lat) repousa exatamente sobre o segmento de reta [(x1,y1), (x2,y2)].
 *
 * @param {number} lon
 * @param {number} lat
 * @param {number} x1
 * @param {number} y1
 * @param {number} x2
 * @param {number} y2
 * @param {number} [epsilon=1e-9]
 * @returns {boolean}
 */
export function isPointOnSegment(lon, lat, x1, y1, x2, y2, epsilon = 1e-9) {
  const minX = Math.min(x1, x2) - epsilon;
  const maxX = Math.max(x1, x2) + epsilon;
  const minY = Math.min(y1, y2) - epsilon;
  const maxY = Math.max(y1, y2) + epsilon;

  if (lon < minX || lon > maxX || lat < minY || lat > maxY) {
    return false;
  }

  const dx = x2 - x1;
  const dy = y2 - y1;
  const segLenSq = dx * dx + dy * dy;
  if (segLenSq <= epsilon * epsilon) {
    const distSq = (lon - x1) * (lon - x1) + (lat - y1) * (lat - y1);
    return distSq <= epsilon * epsilon;
  }

  const cross = Math.abs((lon - x1) * dy - (lat - y1) * dx);
  const perpDist = cross / Math.sqrt(segLenSq);
  return perpDist <= epsilon;
}

/**
 * Avalia um anel linear de coordenadas GeoJSON ([lon, lat][]), retornando se o ponto
 * está dentro do anel (ou exatamente sobre sua borda).
 *
 * @param {number} lon
 * @param {number} lat
 * @param {number[][]} ring
 * @returns {{ inside: boolean, onBoundary: boolean }}
 */
export function pointInPolygonRing(lon, lat, ring) {
  if (!Array.isArray(ring) || ring.length < 3) {
    return { inside: false, onBoundary: false };
  }

  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const ptI = ring[i];
    const ptJ = ring[j];
    if (!Array.isArray(ptI) || !Array.isArray(ptJ)) continue;

    const xi = Number(ptI[0]);
    const yi = Number(ptI[1]);
    const xj = Number(ptJ[0]);
    const yj = Number(ptJ[1]);

    if (isPointOnSegment(lon, lat, xi, yi, xj, yj)) {
      return { inside: true, onBoundary: true };
    }

    const intersect =
      yi > lat !== yj > lat &&
      lon < ((xj - xi) * (lat - yi)) / (yj - yi) + xi;
    if (intersect) {
      inside = !inside;
    }
  }

  return { inside, onBoundary: false };
}

/**
 * Avalia um polígono GeoJSON (anel externo `rings[0]` menos eventuais anéis internos `rings[1..k]`).
 *
 * @param {number} lon
 * @param {number} lat
 * @param {number[][][]} rings
 * @returns {boolean}
 */
export function isPointInPolygonRings(lon, lat, rings) {
  if (!Array.isArray(rings) || rings.length === 0) return false;

  const outer = pointInPolygonRing(lon, lat, rings[0]);
  if (!outer.inside) return false;
  if (outer.onBoundary) return true;

  for (let h = 1; h < rings.length; h++) {
    const hole = pointInPolygonRing(lon, lat, rings[h]);
    if (hole.onBoundary) {
      // Ponto exatamente sobre a borda interna pertence à fronteira do polígono
      return true;
    }
    if (hole.inside) {
      // Ponto estritamente dentro do buraco não pertence ao polígono
      return false;
    }
  }

  return true;
}

/**
 * Verifica se (lon, lat) está contido em uma geometria GeoJSON (`Polygon` ou `MultiPolygon`),
 * respeitando buracos internos e pontos exatamente sobre arestas/vértices.
 *
 * @param {number} lon
 * @param {number} lat
 * @param {{ type: string, coordinates: any }} geometry
 * @returns {boolean}
 */
export function isPointInFeatureGeometry(lon, lat, geometry) {
  if (!geometry || !Array.isArray(geometry.coordinates)) return false;
  if (!Number.isFinite(Number(lon)) || !Number.isFinite(Number(lat))) return false;

  const numLon = Number(lon);
  const numLat = Number(lat);

  if (geometry.type === "Polygon") {
    return isPointInPolygonRings(numLon, numLat, geometry.coordinates);
  }

  if (geometry.type === "MultiPolygon") {
    for (const polyRings of geometry.coordinates) {
      if (isPointInPolygonRings(numLon, numLat, polyRings)) {
        return true;
      }
    }
    return false;
  }

  return false;
}

/**
 * Calcula a caixa delimitadora [minLon, minLat, maxLon, maxLat] de uma geometria Polygon/MultiPolygon.
 *
 * @param {{ type: string, coordinates: any }} geometry
 * @returns {[number, number, number, number] | null}
 */
function computeGeometryBBox(geometry) {
  if (!geometry || !Array.isArray(geometry.coordinates)) return null;
  let minLon = Infinity;
  let minLat = Infinity;
  let maxLon = -Infinity;
  let maxLat = -Infinity;

  const scanRing = (ring) => {
    if (!Array.isArray(ring)) return;
    for (const pt of ring) {
      if (!Array.isArray(pt) || pt.length < 2) continue;
      const x = Number(pt[0]);
      const y = Number(pt[1]);
      if (!Number.isFinite(x) || !Number.isFinite(y)) continue;
      if (x < minLon) minLon = x;
      if (x > maxLon) maxLon = x;
      if (y < minLat) minLat = y;
      if (y > maxLat) maxLat = y;
    }
  };

  if (geometry.type === "Polygon") {
    for (const ring of geometry.coordinates) scanRing(ring);
  } else if (geometry.type === "MultiPolygon") {
    for (const poly of geometry.coordinates) {
      if (Array.isArray(poly)) {
        for (const ring of poly) scanRing(ring);
      }
    }
  }

  if (!Number.isFinite(minLon)) return null;
  return [minLon, minLat, maxLon, maxLat];
}

/**
 * Constrói o índice espacial e cadastral das feições de `/maps/bairros.geojson`.
 * Ordena as entradas por área de bounding box crescente para que enclaves menores sejam testados antes.
 *
 * @param {any[]} [features=[]]
 * @returns {{
 *   features: any[],
 *   featuresById: Map<number, any>,
 *   entries: Array<{ id: number, feature: any, bbox: [number, number, number, number], area: number }>
 * }}
 */
export function buildSpatialNeighborhoodIndex(features = []) {
  const validFeatures = Array.isArray(features) ? features.filter(Boolean) : [];
  const featuresById = new Map();
  const entries = [];

  for (const feature of validFeatures) {
    const numId = Number(feature.id);
    if (Number.isInteger(numId)) {
      featuresById.set(numId, feature);
    }
    const bbox = computeGeometryBBox(feature.geometry);
    if (bbox && Number.isInteger(numId)) {
      const area = Math.max(0, (bbox[2] - bbox[0]) * (bbox[3] - bbox[1]));
      entries.push({ id: numId, feature, bbox, area });
    }
  }

  entries.sort((a, b) => a.area - b.area || a.id - b.id);

  return {
    features: validFeatures,
    featuresById,
    entries,
  };
}

/**
 * Resolve o identificador cartográfico (`feature.id`) de um pin seguindo a prioridade estrita:
 * 1. `bairro_id` somente quando corresponder a um identificador cartográfico validado na malha.
 * 2. `Point-in-Polygon` para coordenadas válidas dentro de Artur Nogueira (rejeitando coordenadas
 *    sintéticas não validadas que contradigam um `pin.bairro` canônico explícito).
 * 3. Nome canônico / aliases (`isNeighborhoodSelected`).
 *
 * @param {any} pin
 * @param {ReturnType<typeof buildSpatialNeighborhoodIndex>} spatialIndex
 * @returns {number | null}
 */
export function resolvePinNeighborhoodId(pin, spatialIndex) {
  if (!pin || !spatialIndex || !(spatialIndex.featuresById instanceof Map)) {
    return null;
  }

  // 1. Prioridade Máxima: bairro_id cartográfico validado
  const rawBairroId = pin.bairro_id ?? pin.indicacao?.bairro_id;
  if (
    rawBairroId !== null &&
    rawBairroId !== undefined &&
    rawBairroId !== "" &&
    typeof rawBairroId !== "boolean"
  ) {
    const numId = Number(rawBairroId);
    if (Number.isInteger(numId) && spatialIndex.featuresById.has(numId)) {
      return numId;
    }
  }

  const rawBairroName = String(pin.bairro || pin.indicacao?.bairro || "").trim();
  const textMatchedFeatures = rawBairroName
    ? spatialIndex.features.filter((f) => isNeighborhoodSelected(f, rawBairroName))
    : [];

  // 2. Segunda Prioridade: Point-in-Polygon em coordenadas confiáveis e válidas
  const lat = pin.latitude ?? pin.lat;
  const lon = pin.longitude ?? pin.lng ?? pin.lon;
  const hasTrustedFlag = pin.untrusted_coords !== true && pin.has_trusted_coords !== false;

  if (hasTrustedFlag && isValidMunicipalCoordinate(lat, lon)) {
    const numLon = Number(lon);
    const numLat = Number(lat);
    const eps = 1e-9;

    for (const entry of spatialIndex.entries) {
      const [minLon, minLat, maxLon, maxLat] = entry.bbox;
      if (
        numLon < minLon - eps ||
        numLon > maxLon + eps ||
        numLat < minLat - eps ||
        numLat > maxLat + eps
      ) {
        continue;
      }

      if (isPointInFeatureGeometry(numLon, numLat, entry.feature.geometry)) {
        // Salvaguarda contra dados simulados (mockData.js sem bairro_id e com coordenadas aleatórias):
        // se o pin declara um bairro explícito que existe na malha oficial, mas o polígono encontrado
        // por geometria contradiz esse bairro explícito, não atribui por geometria sintética inválida.
        if (textMatchedFeatures.length > 0 && !isNeighborhoodSelected(entry.feature, rawBairroName)) {
          break;
        }
        return entry.id;
      }
    }
  }

  // 3. Terceira Prioridade: Nome canônico / aliases
  if (textMatchedFeatures.length > 0) {
    const exactFullMatch = textMatchedFeatures.find(
      (f) =>
        String(f?.properties?.fullName || "").trim().toLowerCase() ===
        rawBairroName.toLowerCase()
    );
    return Number((exactFullMatch || textMatchedFeatures[0]).id);
  }

  return null;
}

/**
 * Calcula 5 intervalos inteiros, crescentes, disjuntos e sem sobreposição para o Mapa Coroplético.
 *
 * Algoritmo:
 * - Para `maxCount <= 4` (incluindo `0`), retorna 5 classes inteiras unitárias fixas:
 *   `[1,1], [2,2], [3,3], [4,4], [5, Infinity]` ("5+"), permitindo classes vazias sem criar
 *   intervalos sobrepostos ou diferenças artificiais.
 * - Para `maxCount >= 5`, particiona `[1, maxCount]` em 5 classes contíguas:
 *   `min_i = floor(((i - 1) * maxCount) / 5) + 1` e `max_i = floor((i * maxCount) / 5)`.
 *
 * @param {number} [maxCount=0]
 * @returns {Array<{ classIndex: number, min: number, max: number, label: string }>}
 */
export function computeChoroplethBreaks(maxCount = 0) {
  const numMax = Number(maxCount);
  const safeMax = Number.isFinite(numMax) && numMax > 0 ? Math.floor(numMax) : 0;

  if (safeMax <= 4) {
    return [
      { classIndex: 1, min: 1, max: 1, label: "1" },
      { classIndex: 2, min: 2, max: 2, label: "2" },
      { classIndex: 3, min: 3, max: 3, label: "3" },
      { classIndex: 4, min: 4, max: 4, label: "4" },
      { classIndex: 5, min: 5, max: Infinity, label: "5+" },
    ];
  }

  const breaks = [];
  for (let i = 1; i <= 5; i++) {
    const min = Math.floor(((i - 1) * safeMax) / 5) + 1;
    const max = Math.floor((i * safeMax) / 5);
    const label = min === max ? `${min}` : `${min} – ${max}`;
    breaks.push({
      classIndex: i,
      min,
      max,
      label,
    });
  }

  return breaks;
}

/**
 * Retorna o índice da faixa coroplética (`0` para sem indicações, `1..5` conforme os intervalos).
 *
 * @param {number} count
 * @param {Array<{ classIndex: number, min: number, max: number }>} [breaks]
 * @returns {number}
 */
export function getChoroplethClassIndex(count, breaks) {
  const numCount = Number(count);
  if (!Number.isFinite(numCount) || numCount <= 0) return 0;

  const intCount = Math.floor(numCount);
  const activeBreaks =
    Array.isArray(breaks) && breaks.length === 5 ? breaks : computeChoroplethBreaks(intCount);

  for (const b of activeBreaks) {
    if (intCount >= b.min && intCount <= b.max) {
      return b.classIndex;
    }
  }

  return 5;
}

/**
 * Agrega as estatísticas coropléticas dos 132 bairros a partir de indicações únicas (`indicacao_id`).
 *
 * Regras metodológicas:
 * - Cada indicação única no bairro conta apenas 1 vez (`count`) e tem apenas 1 voto para a
 *   categoria temática dominante (`dominantCategory`), independentemente de quantos pins possua.
 * - O percentual de cada bairro utiliza explicitamente o denominador:
 *   `Percentual = (indicações únicas no bairro / indicações únicas geolocalizadas no recorte) * 100`.
 *
 * @param {any[]} [features=[]] Feições GeoJSON de `/maps/bairros.geojson`
 * @param {any[]} [pins=[]] Lista de pins do recorte global (`cityPins`)
 * @param {ReturnType<typeof buildSpatialNeighborhoodIndex> | null} [prebuiltIndex=null]
 * @returns {{
 *   statsById: Record<number, {
 *     id: number,
 *     name: string,
 *     fullName: string,
 *     count: number,
 *     percentage: number,
 *     classIndex: number,
 *     dominantCategory: { key: string, label: string, count: number, share: number } | null
 *   }>,
 *   maxCount: number,
 *   totalGeolocatedIndicacoes: number,
 *   totalBairrosComDemanda: number,
 *   breaks: Array<{ classIndex: number, min: number, max: number, label: string }>,
 *   classCounts: Record<number, number>
 * }}
 */
export function computeNeighborhoodChoroplethStats(
  features = [],
  pins = [],
  prebuiltIndex = null
) {
  const spatialIndex = prebuiltIndex || buildSpatialNeighborhoodIndex(features);
  const validPins = Array.isArray(pins) ? pins : [];

  /** @type {Map<number, Map<string|number, string>>} */
  const indicacoesByFeatureId = new Map();
  for (const feature of spatialIndex.features) {
    const fId = Number(feature.id);
    if (Number.isInteger(fId)) {
      indicacoesByFeatureId.set(fId, new Map());
    }
  }

  const geolocatedIndicacoesSet = new Set();

  for (const pin of validPins) {
    if (!pin) continue;
    const featureId = resolvePinNeighborhoodId(pin, spatialIndex);
    if (featureId === null || !indicacoesByFeatureId.has(featureId)) continue;

    const rawIndicacaoId = pin.indicacao_id ?? pin.indicacao?.id ?? pin.id;
    if (rawIndicacaoId === null || rawIndicacaoId === undefined || rawIndicacaoId === "") {
      continue;
    }

    geolocatedIndicacoesSet.add(rawIndicacaoId);

    const featureIndicacoes = indicacoesByFeatureId.get(featureId);
    if (!featureIndicacoes.has(rawIndicacaoId)) {
      const rawCat =
        pin.thematic_category ||
        pin.indicacao?.thematic_category ||
        pin.indicacao?.categoria?.nome ||
        "OUTROS";
      const catStyle = getCategoryStyle(rawCat);
      featureIndicacoes.set(rawIndicacaoId, catStyle.key || "OUTROS");
    }
  }

  const totalGeolocatedIndicacoes = geolocatedIndicacoesSet.size;
  let maxCount = 0;
  let totalBairrosComDemanda = 0;

  /** @type {Record<number, any>} */
  const statsById = {};

  for (const feature of spatialIndex.features) {
    const fId = Number(feature.id);
    if (!Number.isInteger(fId)) continue;

    const featureIndicacoes = indicacoesByFeatureId.get(fId) || new Map();
    const count = featureIndicacoes.size;
    if (count > maxCount) maxCount = count;
    if (count > 0) totalBairrosComDemanda += 1;

    const percentage =
      totalGeolocatedIndicacoes > 0
        ? Number(((count / totalGeolocatedIndicacoes) * 100).toFixed(1))
        : 0;

    let dominantCategory = null;
    if (count > 0) {
      const catCounts = new Map();
      for (const catKey of featureIndicacoes.values()) {
        catCounts.set(catKey, (catCounts.get(catKey) || 0) + 1);
      }

      let bestKey = "OUTROS";
      let bestCount = -1;
      let bestOrderIdx = Infinity;

      for (const [catKey, catCount] of catCounts.entries()) {
        const orderIdx = THEMATIC_CATEGORIES_ORDER.indexOf(catKey);
        const normalizedOrder = orderIdx === -1 ? 999 : orderIdx;
        if (
          catCount > bestCount ||
          (catCount === bestCount && normalizedOrder < bestOrderIdx)
        ) {
          bestKey = catKey;
          bestCount = catCount;
          bestOrderIdx = normalizedOrder;
        }
      }

      const catMeta = THEMATIC_CATEGORIES[bestKey] || THEMATIC_CATEGORIES.OUTROS;
      dominantCategory = {
        key: bestKey,
        label: catMeta.label,
        count: bestCount,
        share: Number(((bestCount / count) * 100).toFixed(1)),
      };
    }

    statsById[fId] = {
      id: fId,
      name: feature.properties?.name || "",
      fullName: feature.properties?.fullName || feature.properties?.name || "",
      count,
      percentage,
      classIndex: 0,
      dominantCategory,
    };
  }

  const breaks = computeChoroplethBreaks(maxCount);
  const classCounts = { 0: 0, 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };

  for (const stat of Object.values(statsById)) {
    const classIndex = getChoroplethClassIndex(stat.count, breaks);
    stat.classIndex = classIndex;
    classCounts[classIndex] = (classCounts[classIndex] || 0) + 1;
  }

  return {
    statsById,
    maxCount,
    totalGeolocatedIndicacoes,
    totalBairrosComDemanda,
    breaks,
    classCounts,
  };
}

/**
 * Retorna o `PathOptions` Leaflet para cada polígono no modo Coroplético,
 * mantendo a escala sequencial Azul-claro → Azul → Índigo no preenchimento e reservando
 * Ciano exclusivamente para o contorno do bairro selecionado (sem alterar sua classe).
 *
 * @param {any} feature Feição GeoJSON do bairro
 * @param {object} [options]
 * @param {number} [options.count=0]
 * @param {Array<{ classIndex: number, min: number, max: number, label: string }>} [options.breaks]
 * @param {any} [options.selectedNeighborhood=null]
 * @param {boolean} [options.isHovered=false]
 * @param {boolean} [options.isDarkMode=false]
 * @returns {import("leaflet").PathOptions}
 */
export function getChoroplethFeatureStyle(
  feature,
  {
    count = 0,
    breaks = [],
    selectedNeighborhood = null,
    isHovered = false,
    isDarkMode = false,
  } = {}
) {
  const palette = getChoroplethPalette(isDarkMode);
  const classIndex = getChoroplethClassIndex(count, breaks);
  const swatch = palette[classIndex] || palette[0];
  const isSelected = isNeighborhoodSelected(feature, selectedNeighborhood);

  const selectedStroke = isDarkMode
    ? CHOROPLETH_SELECTED_STROKE.dark
    : CHOROPLETH_SELECTED_STROKE.light;

  let strokeColor = swatch.borderColor;
  // Bordas reforçadas nas classes inferiores (1 e 2) para evitar que os tons claros desapareçam sobre vias e áreas verdes
  let weight = classIndex === 0 ? 1.1 : classIndex <= 2 ? 1.45 : 1.35;
  let strokeOpacity = classIndex === 0 ? 0.78 : 0.92;
  let fillOpacity = swatch.fillOpacity;

  if (isSelected) {
    strokeColor = selectedStroke;
    weight = isHovered ? 4.0 : 3.5;
    strokeOpacity = 1.0;
    fillOpacity = Math.min(0.90, swatch.fillOpacity + 0.06);
  } else if (isHovered) {
    strokeColor = isDarkMode ? "#f8fafc" : "#0f172a";
    weight = 2.4;
    strokeOpacity = 0.96;
    fillOpacity = Math.min(0.92, swatch.fillOpacity + 0.10);
  }

  return {
    fillColor: swatch.fillColor,
    fillOpacity,
    color: strokeColor,
    weight,
    opacity: strokeOpacity,
    dashArray: undefined,
    className: isSelected
      ? "choropleth-polygon choropleth-polygon-selected"
      : "choropleth-polygon",
  };
}
